import React, { createContext, useContext, useState, useEffect } from 'react';
import { theme } from 'antd';
import type { ThemeConfig } from 'antd';

interface ThemeContextValue {
  isDark: boolean;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  isDark: true,
  toggle: () => {},
});

export const useTheme = () => useContext(ThemeContext);

const baseToken = {
  colorPrimary: '#3ecf8e',
  colorLink: '#00c573',
  colorLinkHover: '#3ecf8e',
  borderRadius: 8,
  fontFamily:
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  fontSize: 14,
  controlHeight: 36,
};

const darkToken = {
  ...baseToken,
  colorBgBase: '#171717',
  colorBgLayout: '#171717',
  colorBgContainer: '#1c1c1c',
  colorBgElevated: '#1c1c1c',
  colorBorder: '#2e2e2e',
  colorBorderSecondary: '#363636',
  colorText: '#fafafa',
  colorTextSecondary: '#b4b4b4',
  colorTextTertiary: '#898989',
};

const lightToken = {
  ...baseToken,
  colorBgBase: '#f8f9fa',
  colorBgContainer: '#ffffff',
  colorBgElevated: '#ffffff',
  colorBorder: '#e8e8e8',
  colorBorderSecondary: '#f0f0f0',
  colorText: '#1f1f1f',
  colorTextSecondary: '#595959',
  colorTextTertiary: '#8c8c8c',
};

const baseComponents = {
  Button: {
    borderRadius: 9999,
    primaryShadow: 'none',
  },
  Card: {
    borderRadius: 12,
  },
  Input: {
    borderRadius: 8,
    activeShadow: '0 0 0 2px rgba(62, 207, 142, 0.15)',
  },
  Select: {
    borderRadius: 8,
  },
  Modal: {
    borderRadius: 16,
  },
};

const darkComponents = {
  ...baseComponents,
  Layout: {
    headerBg: '#1c1c1c',
    siderBg: '#1c1c1c',
    bodyBg: '#171717',
    triggerBg: '#1c1c1c',
    triggerColor: '#fafafa',
    lightSiderBg: '#1c1c1c',
    lightTriggerBg: '#1c1c1c',
    lightTriggerColor: '#fafafa',
  },
  Menu: {
    itemBg: '#1c1c1c',
    itemSelectedBg: 'rgba(62, 207, 142, 0.12)',
    itemSelectedColor: '#3ecf8e',
    itemHoverColor: '#fafafa',
    itemHoverBg: 'rgba(62, 207, 142, 0.06)',
  },
  Table: {
    borderRadius: 12,
    headerBg: '#0f0f0f',
    headerColor: '#fafafa',
    rowHoverBg: 'rgba(62, 207, 142, 0.06)',
  },
  Pagination: {
    itemActiveBg: '#1c1c1c',
  },
};

const lightComponents = {
  ...baseComponents,
  Layout: {
    headerBg: '#ffffff',
    siderBg: '#ffffff',
    bodyBg: '#f8f9fa',
    triggerBg: '#ffffff',
    triggerColor: '#1f1f1f',
    lightSiderBg: '#ffffff',
    lightTriggerBg: '#ffffff',
    lightTriggerColor: '#1f1f1f',
  },
  Menu: {
    itemBg: '#ffffff',
    itemSelectedBg: 'rgba(62, 207, 142, 0.12)',
    itemSelectedColor: '#3ecf8e',
    itemHoverColor: '#1f1f1f',
    itemHoverBg: 'rgba(62, 207, 142, 0.06)',
  },
  Table: {
    borderRadius: 12,
    headerBg: '#f5f5f5',
    headerColor: '#1f1f1f',
    rowHoverBg: 'rgba(62, 207, 142, 0.06)',
  },
  Pagination: {
    itemActiveBg: '#ffffff',
  },
};

export const getThemeConfig = (isDark: boolean): ThemeConfig => ({
  algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
  token: isDark ? darkToken : lightToken,
  components: isDark ? darkComponents : lightComponents,
});

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('cms_theme');
    return saved ? saved === 'dark' : true;
  });

  useEffect(() => {
    localStorage.setItem('cms_theme', isDark ? 'dark' : 'light');
    const root = document.documentElement;
    if (isDark) {
      root.style.setProperty('--cms-bg-deep', '#0f0f0f');
      root.style.setProperty('--cms-bg', '#171717');
      root.style.setProperty('--cms-bg-elevated', '#1c1c1c');
      root.style.setProperty('--cms-border', '#2e2e2e');
      root.style.setProperty('--cms-border-mid', '#363636');
      root.style.setProperty('--cms-gray-mid', '#898989');
      root.style.setProperty('--cms-gray-light', '#b4b4b4');
      root.style.setProperty('--cms-text', '#fafafa');
    } else {
      root.style.setProperty('--cms-bg-deep', '#f5f5f5');
      root.style.setProperty('--cms-bg', '#f8f9fa');
      root.style.setProperty('--cms-bg-elevated', '#ffffff');
      root.style.setProperty('--cms-border', '#e8e8e8');
      root.style.setProperty('--cms-border-mid', '#d9d9d9');
      root.style.setProperty('--cms-gray-mid', '#8c8c8c');
      root.style.setProperty('--cms-gray-light', '#595959');
      root.style.setProperty('--cms-text', '#1f1f1f');
    }
  }, [isDark]);

  const toggle = () => setIsDark((prev) => !prev);

  return (
    <ThemeContext.Provider value={{ isDark, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
};
